import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FormGenerator } from '../../helper/FormGenerator';
import { PageList, FormLayout2 } from '../../components/PageComponents';
import { usePageAPI } from '../../hooks/usePageAPI';
import Head from '../layouts/Head';
import LoadingDashboardContent from '../../components/LoadingDashboardContent';
import { API_URL, APP_URL } from '../../constants';
import axios from 'axios';
import { formatCurrency, formatDateTime, idFormat } from '../../utils/appUtils';

const AdminPayout = ({ userRole, userToken }) => {
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const [formData, setFormData] = useState({});
    const [pageData, setPageData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [searchBy, setSearchBy] = useState('vendor_id');
    const [searchValue, setSearchValue] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);
    const [onInit, setOnInit] = useState({});
    const [vendorInfo, setVendorInfo] = useState({});

    const pageType = {
        isNew: pathname.includes('new'),
        isEdit: pathname.includes('edit'),
        isList: !(pathname.includes('new') || pathname.includes('edit')),
    };

    useEffect(() => {
        const params = new URLSearchParams(search);
        const page = Number(params.get('page')) || 1;
        const limit = Number(params.get('limit')) || 20;
        const searchBy = params.get('searchBy') || 'vendor_id';
        const searchValue = params.get('searchValue') || '';

        setErrorMessage(null);
        setCurrentPage(page);
        setLimit(limit);
        setSearchBy(searchBy);
        setSearchValue(searchValue);

        const loadVendors = () => {
            return axios.get(`${API_URL}/admin/users`, {
                params: {
                    page,
                    limit: 100,
                    searchBy,
                    searchValue,
                    roles: 'ROLE_VENDOR',
                },
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                },
            })
                .then((response) => {
                    const vendorMap = {};

                    response.data.data.forEach(vendor => {
                        vendorMap[vendor.id] = `${vendor.name} (${vendor.id})`;
                    });

                    setOnInit(vendorMap);
                    setVendorInfo(response.data.data);
                })
                .catch(error => {
                    console.error('Failed to fetch vendors:', error);
                    toast.error('Failed to load vendors');
                });
        };

        const loadData = () => {
            setLoading(true);
            axios.get(`${API_URL}/payouts`, {
                params: {
                    limit,
                    currentPage: page,
                    searchBy,
                    searchValue,
                },
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                },
            })
                .then((response) => {
                    setPageData({ data: response.data.data, dataCount: response.data.dataCount });
                })
                .catch(error => {
                    console.error('Failed to fetch payouts:', error);
                    toast.error('Failed to load payouts');
                })
                .finally(() => {
                    setLoading(false);
                });
        };

        const loadPageData = (pageId) => {
            setLoading(true);
            axios.get(`${API_URL}/payout/${pageId}`, {
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                }
            })
                .then(response => {
                    if (response.status === 200) {
                        setFormData(response.data);
                    } else {
                        setErrorMessage(response.data.message || 'Error fetching content');
                    }
                })
                .catch(error => {
                    console.error('Error fetching content:', error);
                    setErrorMessage('Failed to fetch content');
                })
                .finally(() => {
                    setLoading(false);
                });
        };

        const initialize = async () => {
            if (pageType.isList) {
                loadData();
            }

            if (pageType.isNew) {
                if (userRole == 'admin') {
                    loadVendors();
                }
            }

            if (pageType.isEdit) {
                if (userRole !== 'admin') {
                    setErrorMessage('Access denied. Vendors cannot edit payouts.');
                    return;
                }
                const pageId = pathname.split('/').pop();
                if (userRole == 'admin') {
                    await loadVendors();
                }
                loadPageData(pageId);
            }
        };

        initialize();
    }, [pathname]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const formDataToSubmit = {
            ...formData
        };

        const toastId = toast.loading(`Payout ${pageType.isEdit ? 'updating ...' : 'creating ...'}`, { autoClose: false });

        try {
            const pageId = pageType.isEdit ? pathname.split('/').pop() : null;
            const result = pageType.isEdit
                ? await axios.put(`${API_URL}/payout/${pageId}`, formDataToSubmit, {
                    headers: { 'Authorization': `Bearer ${userToken}` }
                })
                : await axios.post(`${API_URL}/payout`, formDataToSubmit, {
                    headers: { 'Authorization': `Bearer ${userToken}` }
                });

            if (result.data.success) {
                toast.update(toastId, {
                    render: `Payout ${pageType.isEdit ? 'updated' : 'created'} successfully!`,
                    type: 'success',
                    isLoading: false,
                    autoClose: 3000,
                });

                if (userRole === 'admin') {
                    navigate(`/${userRole}/payout/edit/${pageType.isEdit ? pageId : result.data.data.id}`);
                } else {
                    navigate(`/${userRole}/payout`);
                }
            } else {
                toast.update(toastId, {
                    render: result.data.message || 'Error occurred',
                    type: 'error',
                    isLoading: false,
                    autoClose: 3000,
                });
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.error || 'An unknown error occurred';
            toast.update(toastId, {
                render: errorMessage,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
            });
            console.error('Error occurred:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this payout?")) {
            const toastId = toast.loading('Deleting ...', { autoClose: false });
            try {
                const result = await axios.delete(`${API_URL}/payout/${id}`, {
                    headers: { 'Authorization': `Bearer ${userToken}` }
                });

                if (result.data.success) {
                    setPageData(prev => ({
                        ...prev,
                        data: prev.data.filter((page) => page.id !== id),
                    }));

                    toast.update(toastId, {
                        render: `Payout deleted successfully!`,
                        type: 'success',
                        isLoading: false,
                        autoClose: 3000,
                    });
                } else {
                    toast.update(toastId, {
                        render: result.data.message || 'Failed to delete payout',
                        type: 'error',
                        isLoading: false,
                        autoClose: 3000,
                    });
                }
            } catch (error) {
                toast.update(toastId, {
                    render: 'Failed to delete payout',
                    type: 'error',
                    isLoading: false,
                    autoClose: 3000,
                });
            }
        }
    };

    const updateUrlParams = (page, limit, searchBy, searchValue) => {
        const params = new URLSearchParams();
        params.set('page', page);
        params.set('limit', limit);
        params.set('searchBy', searchBy);
        params.set('searchValue', searchValue);
        navigate({ pathname: `/${userRole}/payout/list`, search: params.toString() });
    };

    if (errorMessage) {
        return (
            <div style={{ color: 'red' }}>
                <p>{errorMessage}</p>
            </div>
        );
    }

    return (
        <>
            <Head title={'Admin Payout'} />
            {(pageType.isNew || (pageType.isEdit && userRole === 'admin')) && (
                loading ? (
                    <LoadingDashboardContent progress={progress} />
                ) : (
                    <FormLayout2
                        title={pageType.isEdit ? "Edit Payout" : "Create New Payout"}
                        submitText={loading ? (pageType.isEdit ? 'Updating ...' : 'Creating ...') : (pageType.isEdit ? 'Update' : 'Create')}
                        onCancel={() => navigate(`/${userRole}/payout`)}
                        onSubmit={handleFormSubmit}
                    >
                        <div className='border p-4 bg-white'>
                            {(() => {
                                const selectedVendor = Array.isArray(vendorInfo)
                                    ? vendorInfo.find(v => String(v.id) === String(formData.vendor_id))
                                    : null;
                                return selectedVendor ? (
                                    <div className='card border-0 mb-3 shadow-sm bg-gradient bg-primary bg-opacity-10'>
                                        <div className='card-body d-flex flex-column flex-md-row align-items-center gap-3'>
                                            <div>
                                                <i className="bi bi-person-badge fs-2 text-primary"></i>
                                            </div>
                                            <div>
                                                <div className="fw-bold fs-5 text-primary">Vendor: {selectedVendor.name} {idFormat(selectedVendor.id)}</div>
                                                <div className="mt-2">
                                                    <span className="badge bg-success bg-opacity-75 fs-6">
                                                        Current balance: {formatCurrency(selectedVendor.balance)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : null;
                            })()}
                            <FormGenerator
                                fields={
                                    userRole === 'admin' ? [
                                        { id: 'vendor_id', label: 'Vendor ID', type: 'select', options: onInit, required: true, class: 'col-md-12', desc: 'Enter the vendor ID for payout.' },
                                        { id: 'amount', label: 'Amount', type: 'number', required: true, class: 'col-md-6', desc: 'Enter the amount for payout.' },
                                        { id: 'paymentMethod', label: 'Payment Method', type: 'text', required: false, class: 'col-md-6', desc: 'Enter the payment method used for payout.' },
                                        { id: 'adminComment', label: 'Admin Comment', type: 'textarea', required: false, class: 'col-md-6' }
                                    ] : [
                                        { id: 'amount', label: 'Amount', type: 'number', required: true, class: 'col-md-6', desc: 'Enter the amount for payout.' }
                                    ]
                                }
                                fieldData={formData}
                                onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
                            />
                        </div>

                        {userRole === 'admin' && (
                            <div className='border p-4 bg-white'>
                                <FormGenerator
                                    fields={[
                                        { id: 'status', label: 'Status', type: 'select', options: { pending: 'Pending', processed: 'Processed', rejected: 'Rejected' }, required: true, class: 'col-md-12', desc: 'Select the payout status.' },
                                    ]}
                                    fieldData={formData}
                                    onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
                                />
                                <hr />
                                {formData.requestedAt && (
                                    <div>{`Requested At: ${formatDateTime(formData.requestedAt)}`}</div>
                                )}

                                {formData.status !== 'Pending' && formData.processedAt && (
                                    <div>{`Processed At: ${formatDateTime(formData.processedAt)}`}</div>
                                )}
                            </div>
                        )}
                    </FormLayout2>
                )
            )}

            {pageType.isList && (
                loading ? (
                    <LoadingDashboardContent progress={progress} />
                ) : (
                    <PageList
                        itemType="payout"
                        itemId="id"
                        pageTitle="Payout List"
                        pageData={pageData.data}
                        totalRows={pageData.dataCount}
                        actionApplyKeyMap={false}
                        columns={['ReqID', 'Vendor', 'Amount', 'Status', 'Requested Date', 'Processed Date']}
                        filterBy={{
                            '--select--': '',
                            ...(userRole === 'admin' ? { 'Vendor Id': 'vendor_id' } : {}),
                            'DateRange': 'datetime',
                        }}
                        columnKeyMap={{
                            ReqID: (item) => (
                                <span>
                                    {idFormat(item.id)}
                                </span>
                            ),
                            Vendor: (item) => (
                                <Link className="a_edit ms-2" to={`/${userRole}/payout/edit/${item.id}`}>
                                    {item.vendor_name} {idFormat(item.vendor_id)}
                                </Link>
                            ),
                            Amount: (item) => formatCurrency(item.amount),
                            Status: (item) =>
                                item.status === 'processed' ? (
                                    <span className="badge bg-success">Processed</span>
                                ) : (
                                    <span className="badge bg-secondary">Pending</span>
                                ),
                            'Requested Date': (item) =>
                                item.requestedAt ? formatDateTime(item.requestedAt) : 'N/A',
                            'Processed Date': (item) =>
                                item.processedAt ? formatDateTime(item.processedAt) : 'N/A',
                        }}
                        onAddNew={() => navigate(`/${userRole}/payout/new`)}
                        onDelete={userRole === 'admin' ? (item) => handlePageDelete(item.id) : undefined}
                    />

                )
            )}
        </>
    );
};

export default AdminPayout;
